import { useEffect } from 'react';

/**
 * useKeyboardShortcut — registers a global keyboard listener for a simple shortcut.
 *
 * Supported tokens in the shortcut string (case-insensitive, separated by `+`):
 *   - `mod`        → Cmd on macOS, Ctrl elsewhere
 *   - `cmd` / `meta`
 *   - `ctrl` / `control`
 *   - `alt` / `option`
 *   - `shift`
 *   - a single key (e.g. `k`, `/`, `escape`, `arrowup`)
 *
 * Example:
 *   useKeyboardShortcut('mod+k', () => setOpen(true));
 *
 * The callback receives the KeyboardEvent. `event.preventDefault()` is called
 * automatically before invocation so the browser default (e.g. Chrome's "Open
 * location" for Cmd+K) doesn't fire.
 */
export function useKeyboardShortcut(shortcut, callback, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled || !shortcut || typeof callback !== 'function') return undefined;

    const tokens = shortcut
      .toLowerCase()
      .split('+')
      .map((t) => t.trim())
      .filter(Boolean);

    const isMac =
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');

    const wantMeta = tokens.includes('cmd') || tokens.includes('meta') ||
      (tokens.includes('mod') && isMac);
    const wantCtrl = tokens.includes('ctrl') || tokens.includes('control') ||
      (tokens.includes('mod') && !isMac);
    const wantAlt = tokens.includes('alt') || tokens.includes('option');
    const wantShift = tokens.includes('shift');

    const key = tokens.find(
      (t) =>
        !['mod', 'cmd', 'meta', 'ctrl', 'control', 'alt', 'option', 'shift'].includes(t),
    );

    if (!key) return undefined;

    const handler = (event) => {
      const evKey = (event.key || '').toLowerCase();
      if (evKey !== key) return;
      if (wantMeta !== event.metaKey) return;
      if (wantCtrl !== event.ctrlKey) return;
      if (wantAlt !== event.altKey) return;
      if (wantShift !== event.shiftKey) return;
      event.preventDefault();
      callback(event);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcut, callback, enabled]);
}

export default useKeyboardShortcut;
